import { useFragmentFromStore } from '@smx-ui-model/hooks/use-fragment-from-store';
import { getModel } from '@smx-ui-model/store-model';
import { BaseContextType, ModelType } from '@smx-ui-model/types';
import { useAccess } from '@smx-ui-permission/hook/use-access';
import { TableEntity } from '@smx-ui-table/table-entity';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import { drawingSetFragment$key } from '../graphql/__generated__/drawingSetFragment.graphql';
import { JobStatus } from '../graphql/__generated__/drawingSetJobFragment.graphql';
import { DrawingSetStatusInput } from '../graphql/__generated__/drawingSetVersionChangeStatusMutation.graphql';
import { DrawingSetSheetModelType } from '../model/types';
import { RootState } from '../store';
import { IsUpdateAfterUploadDrawingSetVersionType } from '../store/drawings-slice';
import { DrawingSetUploadProcess } from './drawing-set-upload-process';
import { SuspenseWrapper } from './suspense-wrapper';
import { TableDrawingSetSheetPanel } from './table-drawing-set-sheet-panel';

interface TableDrawingSetSheetProps {
    nodeDrawingSet: any;
}

export const TableDrawingSetSheet = forwardRef(({ nodeDrawingSet }: TableDrawingSetSheetProps, ref) => {
    const environment = useRelayEnvironment();
    const isUpdateAfterUploadDrawingSetVersion = useSelector<RootState, IsUpdateAfterUploadDrawingSetVersionType>(
        (state) => state.drawings.isUpdateAfterUploadDrawingSetVersion,
    );

    const drawingSet = useFragmentFromStore<drawingSetFragment$key>(nodeDrawingSet);
    const [jobStatus, setJobStatus] = useState<JobStatus>();
    const [currentStatusDrawingSetVersion, setCurrentStatusDrawingSetVersion] = useState<DrawingSetStatusInput | null>(
        null,
    );
    const [fetchKey, setFetchKey] = useState(0);
    const [currentVersionDate, setCurrentVersionDate] = useState<number>(drawingSet.versionDate);

    const intervalRef = useRef<number | NodeJS.Timeout>(undefined);

    const accessEdit = useAccess(['drawing.all.edit', 'drawing.all.manage']);

    useEffect(() => {
        const checkStore = () => {
            const drawingSetVersion = environment?.getStore().getSource().get(`${drawingSet.id}:${currentVersionDate}`);

            setCurrentStatusDrawingSetVersion(drawingSetVersion?.status as DrawingSetStatusInput);
            if (drawingSetVersion) {
                clearInterval(intervalRef.current);
                return;
            }
        };

        intervalRef.current = setInterval(checkStore, 200);

        return () => {
            clearInterval(intervalRef.current);
        };
    }, [currentVersionDate]);

    useEffect(() => {
        if (isUpdateAfterUploadDrawingSetVersion[drawingSet.id]) {
            setJobStatus(undefined);
        }
    }, [isUpdateAfterUploadDrawingSetVersion[drawingSet.id]]);

    useEffect(() => {
        if (jobStatus === 'SUCCESS') {
            setFetchKey((prevValue) => prevValue + 1);
        }
    }, [jobStatus]);

    const model = {
        ...getModel<ModelType<DrawingSetSheetModelType>>('drawingSetSheet'),
    };

    if (currentStatusDrawingSetVersion === 'PUBLISHED' || !accessEdit) {
        model.context!.tableInput = ['id'];
        model.context!.table = ['id', 'number', 'name', 'revision', 'updatedAt'];
    } else {
        model.context!.tableInput = ['number', 'name', 'isRemoved'];
        model.context!.table = ['id', 'number', 'name', 'revision', 'updatedAt', 'isRemoved'];
    }

    const table = (
        <TableEntity<DrawingSetSheetModelType, BaseContextType>
            ref={ref}
            input={{
                viewMode: 'ALL',
            }}
            inputFilter={{
                drawingSetId: { operator: 'EQ', value: drawingSet.id },
                versionDate: { operator: 'LTE', value: currentVersionDate },
            }}
            model={model}
            limit={10}
            contextName={'table'}
            fetchKeyForUpdate={fetchKey}
        />
    );

    return (
        <>
            {jobStatus !== 'SUCCESS' ? (
                <SuspenseWrapper>
                    <DrawingSetUploadProcess
                        jobId={drawingSet.currentJobId! || drawingSet.previousJobId!}
                        setJobStatus={setJobStatus}
                    />
                </SuspenseWrapper>
            ) : (
                <>
                    <div style={{ marginBottom: 10 }}>
                        <TableDrawingSetSheetPanel
                            drawingSetId={drawingSet.id}
                            versionDate={currentVersionDate}
                            setCurrentVersionDate={setCurrentVersionDate}
                            setCurrentStatusDrawingSetVersion={setCurrentStatusDrawingSetVersion}
                        />
                    </div>
                    {currentStatusDrawingSetVersion === 'PUBLISHED' && table}
                    {currentStatusDrawingSetVersion && currentStatusDrawingSetVersion !== 'PUBLISHED' && table}
                </>
            )}
        </>
    );
});
